import React, { useState, useMemo, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { ChevronDown, LogOut, User, Shield } from "lucide-react";
import ThemeSwitcher from "./ThemeSwitcher";

const RoleBadge = ({ role }) => {
  if (!role) return null;
  const roleMap = {
    admin: {
      label: "Admin",
      cls: "badge-info bg-brand-50/80 text-brand-700 dark:bg-brand-500/20 dark:text-brand-100",
    },
    commercial: {
      label: "Commercial",
      cls: "badge-success bg-emerald-100/70 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-200",
    },
  };
  const conf = roleMap[role] || {
    label: role,
    cls: "badge-neutral",
  };
  return <span className={`badge ${conf.cls}`}>{conf.label}</span>;
};

const Navbar = () => {
  const { currentUser, userProfile, isAdmin, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef(null);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const displayName = useMemo(() => {
    if (userProfile?.firstName || userProfile?.lastName) {
      return `${userProfile.firstName || ""} ${
        userProfile.lastName || ""
      }`.trim();
    }
    return currentUser?.displayName || currentUser?.email || "Utilisateur";
  }, [userProfile, currentUser]);

  const initials = useMemo(() => {
    const name = displayName || "U";
    const parts = name.split(" ").filter(Boolean);
    const first = parts[0]?.[0] || "U";
    const last = parts[1]?.[0] || "";
    return (first + last).toUpperCase();
  }, [displayName]);

  return (
    <nav className="nav-shell sticky top-0 inset-x-0 z-50 shadow-sm transition-colors">
      <div className="container-custom py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-brand-600 text-white flex items-center justify-center font-semibold shadow-custom group-hover:shadow-custom-lg transition-all">
              L
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-lg font-semibold text-[var(--color-text-primary)]">
                LMS Platform
              </span>
              <span className="text-xs text-[var(--color-text-muted)]">
                Experience client
              </span>
            </div>
          </Link>

          <div className="hidden sm:flex items-center gap-3 ml-6 text-sm text-[var(--color-text-muted)]">
            <span className="hidden md:inline">Bienvenue</span>
            <span className="font-medium text-[var(--color-text-primary)]">
              {displayName}
            </span>
            {userProfile?.role && <RoleBadge role={userProfile.role} />}
          </div>
        </div>

        <div className="relative flex items-center gap-4" ref={menuRef}>
          <ThemeSwitcher />
          {isAdmin && (
            <Link
              to="/admin"
              className="hidden sm:inline-flex text-sm font-medium text-[var(--color-brand)] hover:text-[var(--color-brand-strong)] transition-colors"
            >
              Admin
            </Link>
          )}

          <button
            onClick={() => setOpen((v) => !v)}
            className="nav-avatar-btn flex items-center gap-2 px-3 py-1.5 rounded-xl transition-all"
          >
            <div className="w-9 h-9 rounded-full bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] flex items-center justify-center font-semibold shadow-sm">
              {initials}
            </div>
            <ChevronDown
              className={`w-4 h-4 text-[var(--color-text-muted)] transition-transform ${
                open ? "rotate-180" : ""
              }`}
            />
          </button>

          {open && (
            <div className="absolute right-0 top-14 w-72 rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-bg-elevated)] shadow-xl animate-fadeIn">
              <div className="flex items-center gap-3 p-4">
                <div className="w-12 h-12 rounded-xl bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] flex items-center justify-center font-semibold">
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-[var(--color-text-primary)] truncate">
                    {displayName}
                  </p>
                  <p className="text-xs text-[var(--color-text-muted)] truncate">
                    {currentUser?.email}
                  </p>
                </div>
              </div>
              <div className="divider" />
              <ul className="text-sm py-2">
                <li>
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 px-4 py-2.5 text-[var(--color-text-secondary)] hover:bg-[var(--color-brand-soft)] rounded-xl transition-colors"
                  >
                    <User className="w-4 h-4" /> Mon profil
                  </Link>
                </li>
                {isAdmin && (
                  <li>
                    <Link
                      to="/admin"
                      className="flex items-center gap-2 px-4 py-2.5 text-[var(--color-text-secondary)] hover:bg-[var(--color-brand-soft)] rounded-xl transition-colors"
                    >
                      <Shield className="w-4 h-4" /> Espace Admin
                    </Link>
                  </li>
                )}
                <li>
                  <button
                    onClick={logout}
                    className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-danger hover:bg-danger/10 rounded-xl transition-colors"
                  >
                    <LogOut className="w-4 h-4" /> Déconnexion
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
